import React, { useEffect, useRef, useState } from 'react';
import { format } from 'date-fns';
import { useAuth } from '../../../context/AuthContext';
import { addNoteToProfile, editNote, deleteNote } from '../../services/profileService';
import './ClientNotes.css';

const NotesEmptyIcon = () => (
  <svg className="clientNotes__emptyIcon" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" />
  </svg>
);

const MoreIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
  </svg>
);

const EditIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1.003 1.003 0 0 0 0-1.42l-2.34-2.34a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.82z" />
  </svg>
);

const DeleteIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
  </svg>
);

const ClientNotes = ({ client, onNoteAdded }) => {
  const { user, companyData } = useAuth();
  const [newNote, setNewNote] = useState('');
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [error, setError] = useState(null);
  const [editingNote, setEditingNote] = useState(null);
  const [editedText, setEditedText] = useState('');
  const [openMenuId, setOpenMenuId] = useState(null);
  const [selectedNote, setSelectedNote] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const menuRef = useRef(null);

  const getEmployeeName = (email) => {
    if (!email) return 'Staff Member';
    if (email === user.email) {
      return user.firstName && user.lastName
        ? `${user.firstName} ${user.lastName}`
        : 'Staff Member';
    }
    return 'Staff Member';
  };

  const formatDate = (date) => {
    if (!date) return '';
    const dateObj = date instanceof Date
      ? date
      : (date.toDate ? date.toDate() : new Date(date));
    return format(dateObj, 'MMM dd, yyyy h:mm a');
  };

  useEffect(() => {
    if (!openMenuId) return undefined;

    const onPointerDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenuId(null);
      }
    };

    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpenMenuId(null);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [openMenuId]);

  const handleAddNote = async () => {
    if (!newNote.trim()) return;

    setIsAddingNote(true);
    setError(null);

    try {
      await addNoteToProfile(companyData.id, client.id, newNote.trim());
      setNewNote('');
      onNoteAdded?.();
    } catch {
      setError('Failed to add note. Please try again.');
    } finally {
      setIsAddingNote(false);
    }
  };

  const handleEditNote = async () => {
    if (!editingNote || !editedText.trim()) return;

    try {
      setError(null);
      await editNote(companyData.id, client.id, editingNote.id, editedText.trim());
      setEditingNote(null);
      setEditedText('');
      onNoteAdded?.();
    } catch {
      setError('Failed to edit note. Please try again.');
    }
  };

  const handleNoteMenuToggle = (event, note) => {
    event.stopPropagation();
    event.preventDefault();
    setSelectedNote(note);
    setOpenMenuId((prev) => (prev === note.id ? null : note.id));
  };

  const handleEditClick = () => {
    setEditingNote(selectedNote);
    setEditedText(selectedNote.text);
    setOpenMenuId(null);
  };

  const handleDeleteClick = () => {
    setDeleteConfirmOpen(true);
    setOpenMenuId(null);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedNote) return;

    try {
      setError(null);
      const noteToDelete = {
        ...selectedNote,
        createdAt: selectedNote.createdAt instanceof Date
          ? selectedNote.createdAt
          : (selectedNote.createdAt?.toDate ? selectedNote.createdAt.toDate() : new Date(selectedNote.createdAt)),
        updatedAt: selectedNote.updatedAt instanceof Date
          ? selectedNote.updatedAt
          : (selectedNote.updatedAt?.toDate ? selectedNote.updatedAt.toDate() : new Date(selectedNote.updatedAt)),
      };

      await deleteNote(companyData.id, client.id, noteToDelete);
      setDeleteConfirmOpen(false);
      onNoteAdded?.();
    } catch {
      setError('Failed to delete note. Please try again.');
      setDeleteConfirmOpen(false);
    } finally {
      setSelectedNote(null);
    }
  };

  const notes = Array.isArray(client.notes) ? client.notes : [];

  return (
    <div className="clientNotes">
      {notes.length > 0 ? (
        <ul className="clientNotes__list">
          {notes.map((note) => (
            <li className="clientNotes__card" key={note.id || note.createdAt}>
              <div className="clientNotes__cardHeader">
                <p className="clientNotes__author">{getEmployeeName(note.createdBy)}</p>
                <div className="clientNotes__meta">
                  <p className="clientNotes__date">{formatDate(note.createdAt)}</p>
                  <div className="clientNotes__menuWrap" ref={openMenuId === note.id ? menuRef : null}>
                    <button
                      type="button"
                      className="clientNotes__menuBtn"
                      aria-expanded={openMenuId === note.id}
                      aria-haspopup="menu"
                      aria-label="Note options"
                      onClick={(e) => handleNoteMenuToggle(e, note)}
                    >
                      <MoreIcon />
                    </button>
                    {openMenuId === note.id && (
                      <ul className="clientNotes__menu" role="menu">
                        <li role="none">
                          <button
                            type="button"
                            className="clientNotes__menuItem"
                            role="menuitem"
                            onClick={handleEditClick}
                          >
                            <EditIcon />
                            Edit
                          </button>
                        </li>
                        <li role="none">
                          <button
                            type="button"
                            className="clientNotes__menuItem clientNotes__menuItem--danger"
                            role="menuitem"
                            onClick={handleDeleteClick}
                          >
                            <DeleteIcon />
                            Delete
                          </button>
                        </li>
                      </ul>
                    )}
                  </div>
                </div>
              </div>
              <p className="clientNotes__text">{note.text}</p>
              {note.updatedAt && note.updatedAt !== note.createdAt && (
                <span className="clientNotes__edited">
                  Edited {formatDate(note.updatedAt)}
                </span>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <div className="clientNotes__empty">
          <NotesEmptyIcon />
          <h3 className="clientNotes__emptyTitle">No Notes</h3>
          <p className="clientNotes__emptyText">There are no notes for this client yet</p>
        </div>
      )}

      <div className="clientNotes__composer">
        <div className="clientNotes__composerRow">
          <textarea
            className="clientNotes__textarea"
            rows={3}
            placeholder="Add a note..."
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
          />
          <button
            type="button"
            className="clientNotes__addBtn"
            onClick={handleAddNote}
            disabled={isAddingNote || !newNote.trim()}
          >
            {isAddingNote ? <span className="clientNotes__spinner" aria-hidden /> : null}
            Add
          </button>
        </div>
        {error && <p className="clientNotes__error" role="alert">{error}</p>}
      </div>

      {editingNote && (
        <div
          className="clientNotes__dialogBackdrop"
          role="presentation"
          onClick={() => setEditingNote(null)}
        >
          <div
            className="clientNotes__dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="client-notes-edit-title"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="clientNotes__dialogHeader">
              <h2 id="client-notes-edit-title">Edit Note</h2>
            </header>
            <div className="clientNotes__dialogBody">
              <textarea
                className="clientNotes__textarea"
                rows={4}
                value={editedText}
                onChange={(e) => setEditedText(e.target.value)}
              />
            </div>
            <div className="clientNotes__dialogActions">
              <button
                type="button"
                className="clientNotes__dialogBtn"
                onClick={() => setEditingNote(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="clientNotes__dialogBtn clientNotes__dialogBtn--primary"
                onClick={handleEditNote}
                disabled={!editedText.trim()}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirmOpen && (
        <div
          className="clientNotes__dialogBackdrop"
          role="presentation"
          onClick={() => setDeleteConfirmOpen(false)}
        >
          <div
            className="clientNotes__dialog"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="client-notes-delete-title"
            aria-describedby="client-notes-delete-desc"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="clientNotes__dialogHeader">
              <h2 id="client-notes-delete-title">Delete Note</h2>
            </header>
            <div className="clientNotes__dialogBody">
              <p id="client-notes-delete-desc">Are you sure you want to delete this note?</p>
            </div>
            <div className="clientNotes__dialogActions">
              <button
                type="button"
                className="clientNotes__dialogBtn"
                onClick={() => setDeleteConfirmOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="clientNotes__dialogBtn clientNotes__dialogBtn--danger"
                onClick={handleDeleteConfirm}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientNotes;
